import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IconName, IconSizes, PearlIcon } from 'dbi-pearl-ui-kit';
import { useActivePromotion } from 'shared/hooks/useActivePromotion';
import { formatPercent } from 'shared/utils/promotionPricing';
import { FREE_PREMIUM_TRIAL_ENABLED } from 'shared/constants/featureFlags';
import { StorageService, StorageKeys } from 'lib/StorageService';
import {
    WelcomeContainer,
    IntroBlock,
    WelcomeTitle,
    WelcomeIntro,
    TrialAccent,
    FeatureGrid,
    FeatureTile,
    FeatureIconCircle,
    FeatureCopy,
    FeatureTitle,
    FeatureDescription,
    TrialError,
    TrialFooterActions,
    ContinueTrialButton,
    PickPlanButton,
    ContinueArrowIcon,
} from './PremiumTrialWelcome.styled';
import AiProductivityFeatureIcon from './AiProductivityFeatureIcon';

const FEATURE_ITEMS = [
    {
        key: 'verifiedListing',
        icon: IconName.CHECKMARK_CIRCLE,
    },
    {
        key: 'connectedEmail',
        icon: IconName.EMAIL,
    },
    {
        key: 'leadIntelligence',
        icon: IconName.MESSAGE,
    },
    {
        key: 'connectedCalendar',
        icon: IconName.CALENDAR,
    },
    {
        key: 'appointmentBooking',
        icon: IconName.CLOCK,
    },
    {
        key: 'aiProductivity',
        customIcon: true,
    },
];

// The biggest live promotion, annual or monthly, and the billing frequency it is for, so the
// plans page can open on that tab. `coversBoth` when monthly gets the same discount. A percent
// of 0 means no promotion.
const usePromotionOffer = () => {
    const annual = useActivePromotion('year');
    const monthly = useActivePromotion('month');
    const isAnnual = annual.discountPercent >= monthly.discountPercent;
    return {
        percent: Math.max(annual.discountPercent, monthly.discountPercent),
        billingFrequency: isAnnual ? 'year' : 'month',
        coversBoth:
            annual.discountPercent > 0 && annual.discountPercent === monthly.discountPercent,
        isLoaded: annual.isLoaded && monthly.isLoaded,
    };
};

export const PremiumTrialFooterActions = ({
    onContinueTrial,
    onPickPlan,
    trialLoading,
    className = '',
}) => {
    const { t } = useTranslation();
    const offer = usePromotionOffer();
    // Held until the promotion is known, so a click can't land on a button that is about to
    // change.
    const isDisabled = trialLoading || !offer.isLoaded;

    if (offer.isLoaded && offer.percent > 0) {
        const handleGetDiscount = () => {
            try {
                StorageService().set(StorageKeys.planBillingFrequency, offer.billingFrequency);
            } catch (error) {
                // The plans page then opens on its default (annual) tab.
            }
            onPickPlan();
        };

        return (
            <TrialFooterActions className={className}>
                <ContinueTrialButton
                    type="button"
                    data-testid="get-discount-button"
                    onClick={handleGetDiscount}
                    disabled={isDisabled}
                >
                    {t('Forms.BasicDetails.PremiumTrialWelcome.GetDiscount', {
                        percent: formatPercent(offer.percent),
                    })}
                </ContinueTrialButton>
                {/* Free trial discontinued: "No Thanks, I'll Start Free Now" is only offered when
                    FREE_PREMIUM_TRIAL_ENABLED is switched back on. */}
                {FREE_PREMIUM_TRIAL_ENABLED && (
                    <PickPlanButton
                        type="button"
                        data-testid="start-free-button"
                        onClick={onContinueTrial}
                        disabled={isDisabled}
                    >
                        {t('Forms.BasicDetails.PremiumTrialWelcome.StartFree')}
                    </PickPlanButton>
                )}
            </TrialFooterActions>
        );
    }

    // Free trial discontinued: without a sale there is nothing to offer here but the plans.
    if (!FREE_PREMIUM_TRIAL_ENABLED) {
        return (
            <TrialFooterActions className={className}>
                <ContinueTrialButton
                    type="button"
                    data-testid="pick-plan-button"
                    onClick={onPickPlan}
                    disabled={isDisabled}
                >
                    {t('Forms.BasicDetails.PremiumTrialWelcome.Continue')}
                    <ContinueArrowIcon aria-hidden>
                        <PearlIcon iconName={IconName.ARROW_RIGHT} size={IconSizes.SM} />
                    </ContinueArrowIcon>
                </ContinueTrialButton>
            </TrialFooterActions>
        );
    }

    return (
        <TrialFooterActions className={className}>
            <ContinueTrialButton
                type="button"
                data-testid="continue-trial-button"
                onClick={onContinueTrial}
                disabled={isDisabled}
            >
                {t('Forms.BasicDetails.PremiumTrialWelcome.Continue')}
                <ContinueArrowIcon aria-hidden>
                    <PearlIcon iconName={IconName.ARROW_RIGHT} size={IconSizes.SM} />
                </ContinueArrowIcon>
            </ContinueTrialButton>
            <PickPlanButton
                type="button"
                data-testid="pick-plan-button"
                onClick={onPickPlan}
                disabled={isDisabled}
            >
                {t('Forms.BasicDetails.PremiumTrialWelcome.PickPlan')}
            </PickPlanButton>
        </TrialFooterActions>
    );
};

const PremiumTrialWelcome = ({ trialError, onContinueTrial, onPickPlan, trialLoading }) => {
    const { t } = useTranslation();
    const offer = usePromotionOffer();
    const hasPromotion = offer.isLoaded && offer.percent > 0;

    return (
        <WelcomeContainer data-testid="premium-trial-welcome">
            <IntroBlock>
                <WelcomeTitle>{t('Forms.BasicDetails.PremiumTrialWelcome.Title')}</WelcomeTitle>
                {/* Kept hidden (but holding its space) until the promotion is known. */}
                <WelcomeIntro
                    data-testid="premium-trial-intro"
                    style={offer.isLoaded ? undefined : { visibility: 'hidden' }}
                >
                    {hasPromotion ? (
                        t(
                            FREE_PREMIUM_TRIAL_ENABLED
                                ? 'Forms.BasicDetails.PremiumTrialWelcome.IntroPromotion'
                                : 'Forms.BasicDetails.PremiumTrialWelcome.IntroPromotionNoTrial',
                            {
                                percent: formatPercent(offer.percent),
                                subscription: t(
                                    `Forms.BasicDetails.PremiumTrialWelcome.PromotionSubscription.${
                                        offer.coversBoth ? 'any' : offer.billingFrequency
                                    }`
                                ),
                            }
                        )
                    ) : !FREE_PREMIUM_TRIAL_ENABLED ? (
                        t('Forms.BasicDetails.PremiumTrialWelcome.IntroNoTrial')
                    ) : (
                        <Trans
                            i18nKey="Forms.BasicDetails.PremiumTrialWelcome.Intro"
                            components={{ accent: <TrialAccent /> }}
                        />
                    )}
                </WelcomeIntro>
            </IntroBlock>
            <FeatureGrid>
                {FEATURE_ITEMS.map(({ key, icon, customIcon }) => (
                    <FeatureTile key={key}>
                        <FeatureIconCircle $isAiProductivity={customIcon}>
                            {customIcon ? (
                                <AiProductivityFeatureIcon />
                            ) : (
                                <PearlIcon iconName={icon} size={IconSizes.LG} />
                            )}
                        </FeatureIconCircle>
                        <FeatureCopy>
                            <FeatureTitle>
                                {t(`Forms.BasicDetails.PremiumTrialWelcome.Features.${key}.title`)}
                            </FeatureTitle>
                            <FeatureDescription>
                                {t(
                                    `Forms.BasicDetails.PremiumTrialWelcome.Features.${key}.description`
                                )}
                            </FeatureDescription>
                        </FeatureCopy>
                    </FeatureTile>
                ))}
            </FeatureGrid>
            {trialError ? <TrialError role="alert">{trialError}</TrialError> : null}
            {onContinueTrial && onPickPlan ? (
                <PremiumTrialFooterActions
                    className="premiumTrialWelcomeMobileFooter"
                    onContinueTrial={onContinueTrial}
                    onPickPlan={onPickPlan}
                    trialLoading={trialLoading}
                />
            ) : null}
        </WelcomeContainer>
    );
};

export default PremiumTrialWelcome;
